import React, { useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader } from "@/components/primitives";
import { Scales, ArrowClockwise, Download } from "@phosphor-icons/react";

const TYPE_ORDER = ["asset", "liability", "equity", "revenue", "expense"];
const TYPE_LABEL = {
  asset: "Assets", liability: "Liabilities", equity: "Equity",
  revenue: "Revenue", expense: "Expenses",
};

function fmtZMW(n) {
  const v = Number(n || 0);
  if (Math.abs(v) < 0.005) return "—";
  return v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function TrialBalance() {
  const [asOf, setAsOf] = useState(new Date().toISOString().slice(0, 10));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get(`/gl/trial-balance?as_of=${asOf}`);
      setData(data);
    } catch (e) { toast.error(formatError(e)); }
    setLoading(false);
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [asOf]);

  const grouped = {};
  (data?.rows || []).forEach((r) => {
    (grouped[r.type] ||= []).push(r);
  });

  function exportCsv() {
    if (!data) return;
    const header = "Code,Name,Type,Debit,Credit\n";
    const body = data.rows
      .filter((r) => r.balance_debit || r.balance_credit)
      .map((r) => `${r.code},"${r.name.replace(/"/g, '""')}",${r.type},${r.balance_debit},${r.balance_credit}`)
      .join("\n");
    const blob = new Blob([header + body], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `trial-balance-${asOf}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div data-testid="tb-page">
      <PageHeader
        overline="Finance / Reports"
        title="Trial Balance"
        subtitle="Every account's net position from the general ledger, as at any date you pick."
        testId="tb-header"
      />

      <div className="phizo-card p-4 mb-4 flex items-center gap-3 flex-wrap">
        <Scales size={16} weight="bold" className="text-[#71717a]" />
        <label className="text-xs flex items-center gap-2">
          <span className="phizo-label text-[10px]">As of</span>
          <input type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)}
                 className="border border-[#e4e4e7] px-3 py-1.5 text-xs outline-none focus:border-black"
                 data-testid="tb-asof" />
        </label>
        <button type="button" onClick={load} className="border border-[#e4e4e7] px-3 py-1.5 text-xs hover:bg-[#f4f4f5]" data-testid="tb-refresh">
          <ArrowClockwise size={12} weight="bold" className="inline mr-1" /> Refresh
        </button>
        <button type="button" onClick={exportCsv} disabled={!data}
                className="ml-auto border border-[#e4e4e7] px-3 py-1.5 text-xs hover:bg-[#f4f4f5] disabled:opacity-50"
                data-testid="tb-export">
          <Download size={12} weight="bold" className="inline mr-1" /> Export CSV
        </button>
        {data && (
          <span
            className={`text-[10px] font-bold uppercase tracking-wide px-2 py-1 ${data.totals.balanced ? "bg-[#dcfce7] text-[#166534]" : "bg-[#fee2e2] text-[#991b1b]"}`}
            data-testid="tb-balanced-badge"
          >
            {data.totals.balanced ? "Balanced" : "Out of balance"}
          </span>
        )}
      </div>

      {loading && !data && <div className="phizo-card p-6 text-center text-[#71717a]">Loading…</div>}

      {data && (
        <div className="phizo-card overflow-x-auto" data-testid="tb-table">
          <table className="w-full text-xs">
            <thead className="bg-[#fafafa] border-b border-[#e4e4e7]">
              <tr className="text-left">
                <th className="px-3 py-2 phizo-label text-[9px]">Code</th>
                <th className="px-3 py-2 phizo-label text-[9px]">Account</th>
                <th className="px-3 py-2 phizo-label text-[9px] text-right">Debit</th>
                <th className="px-3 py-2 phizo-label text-[9px] text-right">Credit</th>
              </tr>
            </thead>
            <tbody>
              {TYPE_ORDER.map((t) => {
                const grp = grouped[t] || [];
                if (grp.length === 0) return null;
                const sub_dr = grp.reduce((s, r) => s + r.balance_debit, 0);
                const sub_cr = grp.reduce((s, r) => s + r.balance_credit, 0);
                return (
                  <React.Fragment key={t}>
                    <tr className="bg-[#fafafa]">
                      <td colSpan={4} className="px-3 py-1.5 phizo-label text-[10px] tracking-wider">
                        {TYPE_LABEL[t]}
                      </td>
                    </tr>
                    {grp.map((r) => (
                      <tr key={r.code} className="border-b border-[#f4f4f5] hover:bg-[#fafafa]"
                          data-testid={`tb-row-${r.code}`}>
                        <td className="px-3 py-1.5 mono font-semibold">{r.code}</td>
                        <td className="px-3 py-1.5">
                          {r.parent_code && <span className="text-[#a1a1aa] mono text-[10px] mr-1">↳</span>}
                          {r.name}
                        </td>
                        <td className="px-3 py-1.5 text-right mono">{fmtZMW(r.balance_debit)}</td>
                        <td className="px-3 py-1.5 text-right mono">{fmtZMW(r.balance_credit)}</td>
                      </tr>
                    ))}
                    <tr className="bg-white border-b-2 border-[#e4e4e7]">
                      <td></td>
                      <td className="px-3 py-1.5 text-[10px] font-bold uppercase text-[#71717a]">
                        Total {TYPE_LABEL[t]}
                      </td>
                      <td className="px-3 py-1.5 text-right mono font-bold">{fmtZMW(sub_dr)}</td>
                      <td className="px-3 py-1.5 text-right mono font-bold">{fmtZMW(sub_cr)}</td>
                    </tr>
                  </React.Fragment>
                );
              })}
            </tbody>
            <tfoot className="bg-[#0b1027] text-white">
              <tr>
                <td colSpan={2} className="px-3 py-2 text-xs font-bold">Grand totals</td>
                <td className="px-3 py-2 text-right mono font-bold" data-testid="tb-total-debit">
                  {fmtZMW(data.totals.debit)}
                </td>
                <td className="px-3 py-2 text-right mono font-bold" data-testid="tb-total-credit">
                  {fmtZMW(data.totals.credit)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}
